import React, { useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial } from '../../../context/FinancialContext';
import { fmt } from './walkthroughUtils';

const { FiPlus, FiChevronDown } = FiIcons;

const KINDS = {
  income: {
    action: 'ADD_INCOME',
    button: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200',
    focus: 'focus:ring-emerald-500',
    label: 'Add Income'
  },
  expense: {
    action: 'ADD_EXPENSE',
    button: 'bg-rose-600 hover:bg-rose-700 shadow-rose-200',
    focus: 'focus:ring-rose-500',
    label: 'Add Expense'
  },
  investment: {
    action: 'ADD_INVESTMENT',
    button: 'bg-blue-600 hover:bg-blue-700 shadow-blue-200',
    focus: 'focus:ring-blue-500',
    label: 'Add Investment'
  }
};

const PERIODS = [
  ['weekly', 'Weekly'],
  ['biweekly', 'Bi-weekly'],
  ['monthly', 'Monthly'],
  ['yearly', 'Yearly'],
  ['quarter', '3-Mo Total']
];

const INPUT = 'w-full px-4 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-none outline-none focus:ring-2 shadow-sm';

const QuickAddForm = ({ kind, placeholder }) => {
  const { dispatch } = useFinancial();
  const cfg = KINDS[kind];
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [need, setNeed] = useState('required');

  const amt = parseFloat(amount) || 0;

  const getAccount = () => {
    if (kind === 'income') return 'income_hub';
    if (kind === 'investment') return 'investing_transfer';
    return need === 'required' ? 'bills' : 'discretionary';
  };

  const handleAdd = () => {
    if (!name.trim() || !(amt > 0)) return;
    const isQuarter = period === 'quarter';
    const payload = {
      id: Date.now() + Math.random(),
      name: name.trim(),
      amount: isQuarter ? Math.round((amt / 3) * 100) / 100 : amt,
      frequency: isQuarter ? 'monthly' : period,
      accountType: getAccount()
    };
    if (kind === 'investment') {
      payload.icon = 'TrendingUp';
      payload.color = 'blue';
    }
    dispatch({ type: cfg.action, payload });
    setName('');
    setAmount('');
  };

  const onKey = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
      <input
        className={`${INPUT} ${cfg.focus}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={onKey}
        placeholder={placeholder}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-sm">$</span>
          <input
            type="number"
            className={`${INPUT} ${cfg.focus} pl-7`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={onKey}
            placeholder="0"
          />
        </div>
        <div className="relative">
          <select
            className={`${INPUT} ${cfg.focus} appearance-none cursor-pointer pr-8`}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            <SafeIcon icon={FiChevronDown} />
          </div>
        </div>
      </div>

      {kind === 'expense' && (
        <div className="flex bg-white p-1 rounded-xl border border-slate-100">
          {[['required', 'Required'], ['discretionary', 'Discretionary']].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setNeed(val)}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                need === val
                  ? val === 'required'
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {period === 'quarter' && amt > 0 && (
        <p className="text-[10px] font-bold text-slate-500 ml-1">Averaged to ≈ {fmt(amt / 3)} / month</p>
      )}

      <button
        onClick={handleAdd}
        className={`w-full py-3 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all ${cfg.button}`}
      >
        <SafeIcon icon={FiPlus} /> {cfg.label}
      </button>
    </div>
  );
};

export default QuickAddForm;